import {
  ITimelineRow,
  TaskCtrlAppCard,
  TemplateCanvas,
} from 'src/interface/org'
import {
  WEEK_WIDTH,
  PADDING,
  ROW_HEIGHT,
  numberOfWeeks,
  CARD_WIDTH,
  CARD_HEIGHT,
} from '../createTimeline'
import { cloneYearWeek } from '../time'

const FONT = '120px sans-serif'
const Y_OFFSET = 45
const DISCIPLINE_TEXT_ROTATION = -Math.PI / 2

export const renderTimelineCanvas = async (
  timeline: TemplateCanvas,
  appCards: TaskCtrlAppCard[],
  template = false,
): Promise<string | undefined> => {
  const rowCount = timeline.numberOfDisciplines || 1

  const frameWidth = timeline.numberOfWeeks * WEEK_WIDTH + PADDING * 2
  const frameHeight = rowCount * ROW_HEIGHT + PADDING * 2

  const startWeek = cloneYearWeek(timeline.startWeek)
  const endWeek = cloneYearWeek(timeline.endWeek)
  const start = startWeek.subtract(template ? 0 : timeline.pastWeeks)

  const weekNumbers = numberOfWeeks(start, endWeek)
  const rows = getRows(timeline)

  const canvas = document.createElement('canvas')
  canvas.width = frameWidth
  canvas.height = frameHeight
  canvas.style.backgroundColor = 'white'

  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.fillStyle = 'white'
  ctx.fillRect(0, 0, frameWidth, frameHeight)

  for (let i = 0; i < rowCount; i++) {
    const rowObject = rows[i]
    const ySpacing = i * ROW_HEIGHT + PADDING
    addDisciplineText(`${rowObject.disciplines.name}`, ctx, 0, ySpacing)

    ctx.fillStyle = rowObject.disciplines.color
    ctx.globalAlpha = 0.1
    ctx.fillRect(PADDING, ySpacing, weekNumbers.length * WEEK_WIDTH, ROW_HEIGHT)
    ctx.globalAlpha = 1
    ctx.strokeRect(
      PADDING,
      ySpacing,
      weekNumbers.length * WEEK_WIDTH,
      ROW_HEIGHT,
    )
  }

  weekNumbers.forEach((day, columnIndex) => {
    const xSpacing = columnIndex * WEEK_WIDTH + PADDING

    ctx.textAlign = 'center'
    ctx.fillStyle = day.before(startWeek) ? '#CCC' : 'black'
    ctx.font = FONT
    ctx.fillText(day.toString(), xSpacing + WEEK_WIDTH / 2, PADDING * 0.75)

    ctx.fillStyle = day.before(startWeek) ? '#CCC' : 'transparent'
    ctx.strokeStyle = '#000'
    ctx.lineWidth = 2
    ctx.globalAlpha = 0.2
    ctx.fillRect(xSpacing, PADDING, WEEK_WIDTH, rowCount * ROW_HEIGHT)
    ctx.globalAlpha = 1
    ctx.strokeRect(xSpacing, PADDING, WEEK_WIDTH, rowCount * ROW_HEIGHT)
  })

  ctx.font = '46px sans-serif'
  const cards = getAppCardPosition(
    appCards,
    frameWidth,
    frameHeight,
    (template ? timeline.pastWeeks : 0) * WEEK_WIDTH,
  )
  for (const card of cards) {
    const { x, y } = card
    if (x === undefined || y === undefined) continue
    drawAppCards(ctx, x, y, card.discipline?.color ?? '#000')

    ctx.fillStyle = 'black'
    ctx.fillText(card.title, x + CARD_WIDTH / 2, y + CARD_HEIGHT * 0.75)
  }

  if (!template) {
    return canvasToPng(canvas)
  }
  return await canvasToBlobUrl(canvas)
}

const canvasToPng = async (canvas: HTMLCanvasElement): Promise<string> => {
  const blobUrl = await canvasToBlobUrl(canvas)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = 'timeline.png'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  canvas.remove()
  return blobUrl
}

const canvasToBlobUrl = (canvas: HTMLCanvasElement): Promise<string> => {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob)
        resolve(url)
      } else {
        resolve('')
      }
    }, 'image/png')
  })
}

const addDisciplineText = (
  txt: string,
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
) => {
  const textCanvas = document.createElement('canvas')
  textCanvas.width = PADDING
  textCanvas.height = ROW_HEIGHT
  const textCtx = textCanvas.getContext('2d')
  if (!textCtx) return

  textCtx.fillStyle = 'white'
  textCtx.fillRect(0, 0, textCanvas.width, textCanvas.height)

  textCtx.save()
  textCtx.translate(textCanvas.width / 2, textCanvas.height / 2)
  textCtx.rotate(DISCIPLINE_TEXT_ROTATION)
  textCtx.font = FONT
  textCtx.textAlign = 'center'
  textCtx.textBaseline = 'middle'
  textCtx.fillStyle = 'black'
  textCtx.fillText(txt, 0, 0)
  textCtx.restore()

  ctx.drawImage(textCanvas, x, y)
  textCanvas.remove()
}

const getRows = (timeline: TemplateCanvas): ITimelineRow[] =>
  timeline.cells.filter((cell) => cell.column === 0).map((cell) => cell.data)

const convertGlobalPosition = (
  { x, y }: { x: number; y: number },
  b: number,
) => {
  return {
    x: x - b - PADDING,
    y: y + Y_OFFSET,
  }
}

const convertLocalPosition = (
  { x, y }: { x: number; y: number },
  width: number,
  height: number,
  b: number,
): { x: number; y: number } => {
  return {
    x: x + (width - b) / 2 - CARD_WIDTH / 2,
    y: y + height / 2 - Y_OFFSET,
  }
}

const getAppCardPosition = (
  appCard: TaskCtrlAppCard[],
  width: number,
  height: number,
  backWeeks: number,
) => {
  return appCard.map((card) => {
    const pos = card.position ?? { x: 0, y: 0 }
    return {
      title: card.title,
      discipline: card.discipline,
      ...(isGlobal(pos)
        ? convertGlobalPosition(pos, backWeeks)
        : convertLocalPosition(pos, width, height, backWeeks)),
    }
  })
}

const drawAppCards = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
) => {
  const borderRadius = 16
  const borderWidth = 4
  ctx.save()

  ctx.beginPath()
  ctx.moveTo(x + borderRadius, y)
  ctx.lineTo(x + CARD_WIDTH - borderRadius, y)
  ctx.quadraticCurveTo(x + CARD_WIDTH, y, x + CARD_WIDTH, y + borderRadius)
  ctx.lineTo(x + CARD_WIDTH, y + CARD_HEIGHT - borderRadius)
  ctx.quadraticCurveTo(
    x + CARD_WIDTH,
    y + CARD_HEIGHT,
    x + CARD_WIDTH - borderRadius,
    y + CARD_HEIGHT,
  )
  ctx.lineTo(x + borderRadius, y + CARD_HEIGHT)
  ctx.quadraticCurveTo(x, y + CARD_HEIGHT, x, y + CARD_HEIGHT - borderRadius)
  ctx.lineTo(x, y + borderRadius)
  ctx.quadraticCurveTo(x, y, x + borderRadius, y)
  ctx.closePath()

  ctx.globalAlpha = 0.15
  ctx.fillStyle = color
  ctx.fill()
  ctx.globalAlpha = 1

  ctx.lineWidth = borderWidth
  ctx.strokeStyle = color
  ctx.stroke()

  ctx.restore()
}

const isGlobal = ({ x, y }: { x: number; y: number }): boolean =>
  !Number.isInteger(x) || !Number.isInteger(y)
